import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Compass, Sparkles, Star, Loader2, Mic, Volume2, VolumeX } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useBibleVersion } from "@/lib/bible";
import { useNarration } from "@/lib/useNarration";
import { RetellingBadge, ScriptureQuote, ScriptureRef } from "./Common";
import { SeriesBadge } from "./SeriesBadge";
import { announceStreak } from "./Streak";

type Result = { score: number; total: number; choices: string[] };

const splitSentences = (t: string) => t.match(/[^.!?]+[.!?]+["'”’)]*\s*|[^.!?]+$/g) || [t];

// Builds the narrated script and the char span for each highlightable piece
function buildScript(scene: any) {
  const head = `${scene.title}. `;
  const parts: { key: string; start: number; end: number }[] = [{ key: "title", start: 0, end: head.length }];
  let pos = head.length;
  const sentences = splitSentences(scene.text);
  sentences.forEach((s, i) => { parts.push({ key: `s${i}`, start: pos, end: pos + s.length }); pos += s.length; });
  let script = head + scene.text;
  if (scene.verse) {
    const v = ` Scripture says: ${scene.verse}`;
    parts.push({ key: "verse", start: pos, end: pos + v.length });
    script += v;
  }
  return { script, parts, sentences };
}

export const VOICES = [
  { id: "fable", label: "Storyteller", hint: "warm and expressive" },
  { id: "nova", label: "Gentle", hint: "soft female voice" },
  { id: "onyx", label: "Deep", hint: "rich male voice" },
  { id: "coral", label: "Bright", hint: "cheerful and clear" },
];

function useAmbience(src?: string | null) {
  const [on, setOn] = useState(() => localStorage.getItem("cast_ambience") !== "off");
  const el = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => { el.current?.pause(); el.current = null; }, [src]);
  const play = () => {
    if (!on || !src) return;
    if (!el.current) { el.current = new Audio(src); el.current.loop = true; el.current.volume = 0.16; }
    el.current.play().catch(() => {});
  };
  const pause = () => el.current?.pause();
  const toggle = (playing?: boolean) => { const v = !on; setOn(v); localStorage.setItem("cast_ambience", v ? "on" : "off"); if (!v) pause(); else if (playing && src) { if (!el.current) { el.current = new Audio(src); el.current.loop = true; el.current.volume = 0.16; } el.current.play().catch(() => {}); } };
  return { on, play, pause, toggle, available: !!src };
}

function Narration({ src, text, kids, onPos, ambience }: { src: string; text: string; kids?: boolean; onPos: (charIndex: number) => void; ambience?: string | null }) {
  const fallback = useNarration();
  const audio = useRef<HTMLAudioElement | null>(null);
  const amb = useAmbience(ambience);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "paused">("idle");
  const [progress, setProgress] = useState(0);
  const [rate, setRate] = useState(() => +(localStorage.getItem("cast_rate") || 1));
  const [voice, setVoice] = useState(() => localStorage.getItem("cast_voice") || "fable");
  const bv = useBibleVersion().id;
  const stopAll = () => { audio.current?.pause(); audio.current = null; fallback.stop(); amb.pause(); setState("idle"); setProgress(0); onPos(-1); };
  useEffect(() => () => { audio.current?.pause(); fallback.stop(); }, [src]); // eslint-disable-line

  const toggle = async () => {
    sample.current?.pause();
    if (fallback.speaking) { amb.pause(); return fallback.stop(); }
    if (audio.current) return state === "playing" ? audio.current.pause() : audio.current.play();
    setState("loading");
    await api.get("/auth/me").catch(() => {});
    const a = new Audio(`${src}?voice=${voice}&bv=${bv}`);
    a.playbackRate = rate;
    a.onplaying = () => { setState("playing"); amb.play(); };
    a.onpause = () => { setState("paused"); amb.pause(); };
    a.onended = () => { setState("idle"); setProgress(0); onPos(-1); amb.pause(); };
    a.ontimeupdate = () => {
      const f = a.duration ? a.currentTime / a.duration : 0;
      setProgress(f);
      onPos(Math.min(text.length - 1, Math.floor(f * text.length * 1.03)));
    };
    a.onerror = () => { audio.current = null; setState("idle"); fallback.play(text, onPos); };
    audio.current = a;
    a.play().catch(() => {});
  };
  const sample = useRef<HTMLAudioElement | null>(null);
  const [sampling, setSampling] = useState(false);
  const preview = (v: string) => {
    sample.current?.pause();
    audio.current?.pause();
    fallback.stop();
    const s = new Audio(`${API}/voice-sample/${v}`);
    s.onplaying = () => setSampling(true);
    s.onended = s.onpause = s.onerror = () => setSampling(false);
    sample.current = s;
    s.play().catch(() => setSampling(false));
  };
  useEffect(() => () => sample.current?.pause(), []);
  const changeVoice = (v: string) => { stopAll(); setVoice(v); localStorage.setItem("cast_voice", v); preview(v); };
  const changeRate = (r: number) => { setRate(r); localStorage.setItem("cast_rate", String(r)); fallback.setRate(r); if (audio.current) audio.current.playbackRate = r; };
  const playing = state === "playing" || fallback.speaking;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="narration-controls">
      <button data-testid="story-audio-play-button" onClick={toggle}
        className={`relative flex items-center gap-2 overflow-hidden rounded-full px-4 py-2 font-semibold text-white transition-transform hover:scale-105 ${kids ? "bg-[#E07A5F] text-lg" : "bg-[#1E293B] text-sm"}`}>
        <span className="absolute inset-y-0 left-0 bg-white/20 transition-[width]" style={{ width: `${progress * 100}%` }} />
        {state === "loading" ? <Loader2 className="relative h-4 w-4 animate-spin" /> : playing ? <Pause className="relative h-4 w-4" /> : <Play className="relative h-4 w-4" />}
        <span className="relative" data-testid="narration-state">{state === "loading" ? "Preparing…" : playing ? "Pause" : state === "paused" ? "Resume" : "Listen"}</span>
      </button>
      {[1, 1.25, 1.5].map((r) => (
        <button key={r} data-testid={`narration-rate-${r}`} onClick={() => changeRate(r)}
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${rate === r ? "bg-[#F3ECE0] text-slate-900" : "text-slate-500"}`}>{r}x</button>
      ))}
      <Select value={voice} onValueChange={changeVoice}>
        <SelectTrigger data-testid="narrator-voice-select" className="h-8 w-auto gap-1 rounded-full border-[#E2DBCE] bg-white px-3 text-xs font-semibold"><Mic className="h-3.5 w-3.5 text-[#C86D51]" /><SelectValue /></SelectTrigger>
        <SelectContent>{VOICES.map((v) => <SelectItem key={v.id} value={v.id} data-testid={`narrator-voice-${v.id}`}>{v.label} <span className="text-slate-400">· {v.hint}</span></SelectItem>)}</SelectContent>
      </Select>
      <button data-testid="narrator-voice-preview" onClick={() => (sampling ? sample.current?.pause() : preview(voice))} title="Hear a sample of this voice"
        className="rounded-full px-2.5 py-1 text-xs font-semibold text-[#C86D51] hover:bg-[#F3ECE0]">{sampling ? "Stop sample" : "Preview voice"}</button>
      {amb.available && (
        <button data-testid="ambience-toggle" onClick={() => amb.toggle(playing)} title={amb.on ? "Background sounds on" : "Background sounds off"}
          className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${amb.on ? "bg-[#5B7065]/15 text-[#3f5248]" : "text-slate-400 hover:bg-[#F3ECE0]"}`}>
          {amb.on ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

function Scene({ scene, kids, picked, onPick, src, clip, save }: any) {
  const { script, parts, sentences } = useMemo(() => buildScript(scene), [scene]);
  const [pos, setPos] = useState(-1);
  const active = pos < 0 ? null : parts.find((p) => pos >= p.start && pos < p.end)?.key ?? null;
  const hl = (key: string) => `rounded-md transition-colors duration-300 ${active === key ? (kids ? "bg-amber-200 text-slate-900" : "bg-amber-100 text-slate-900") : ""}`;
  return (
    <motion.div key={scene.title} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }}
      className={`overflow-hidden ${kids ? "rounded-3xl border-4 border-white bg-white shadow-xl" : "card-warm shadow-sm"}`} data-testid="story-scene">
      <div className="relative overflow-hidden">
        {clip ? <video src={clip} poster={scene.image} autoPlay muted loop playsInline data-testid="scene-clip" className="aspect-[16/9] w-full object-cover" />
          : <img src={scene.image} alt={scene.title} className="ken-burns aspect-[16/9] w-full object-cover" />}
      </div>
      <div className={`space-y-5 ${kids ? "p-6 sm:p-10" : "p-6 sm:p-10"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3"><RetellingBadge refText={scene.ref} testId="scene-retelling-badge" /><Narration src={src} text={script} kids={kids} onPos={setPos} ambience={scene.ambience_url ? `${API}${scene.ambience_url}` : null} /></div>
        <h2 className={`${kids ? "font-kids text-3xl" : "font-display text-3xl sm:text-4xl"} font-semibold text-slate-900`} data-testid="scene-title"><span className={hl("title")}>{scene.title}</span></h2>
        <p className={`${kids ? "font-kids text-xl leading-relaxed" : "text-base leading-relaxed md:text-lg"} text-slate-700`} data-testid="scene-text">
          {sentences.map((s: string, i: number) => <span key={i} data-testid={`read-along-sentence-${i}`} data-active={active === `s${i}`} className={hl(`s${i}`)}>{s}</span>)}
        </p>
        {scene.verse ? <div className={`rounded-lg transition-shadow duration-300 ${active === "verse" ? "shadow-[0_0_0_4px_rgba(251,191,36,0.55)]" : ""}`} data-testid="read-along-verse" data-active={active === "verse"}><ScriptureQuote text={scene.verse} refText={scene.ref} large={kids} plain={kids} save={save} /></div> : <ScriptureRef refText={scene.ref} plain={kids} />}
        {scene.choice && (
          <div className={`rounded-2xl p-5 ${kids ? "bg-[#81B29A]/15" : "bg-[#F3ECE0]/70"}`} data-testid="choice-moment">
            <p className={`flex items-center gap-2 font-semibold text-slate-900 ${kids ? "font-kids text-xl" : ""}`}><Compass className="h-5 w-5 text-[#C86D51]" />{scene.choice.prompt}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {scene.choice.options.map((o: any, i: number) => (
                <button key={i} data-testid={`choice-option-${i}`} onClick={() => onPick(i)} disabled={picked !== undefined}
                  className={`rounded-xl border-2 px-4 py-3 text-left font-semibold transition-all ${picked === i ? "border-[#C86D51] bg-white" : "border-transparent bg-white/80 hover:border-[#E2DBCE]"} ${kids ? "text-lg" : ""}`}>{o.label}</button>
              ))}
            </div>
            {picked !== undefined && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-slate-700" data-testid="choice-response">{scene.choice.options[picked].response}</motion.p>}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function Quiz({ quiz, kids, onDone }: { quiz: any[]; kids?: boolean; onDone: (score: number) => void }) {
  const [qi, setQi] = useState(0);
  const [ans, setAns] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const q = quiz[qi];
  const pick = (i: number) => { if (ans !== null) return; setAns(i); if (i === q.answer) setScore((s) => s + 1); };
  const next = () => { if (qi + 1 >= quiz.length) onDone(score); else { setQi(qi + 1); setAns(null); } };
  return (
    <div className={`${kids ? "rounded-3xl bg-white p-8 shadow-xl" : "card-warm p-6 sm:p-10"}`} data-testid="quiz-panel">
      <p className="eyebrow">Quiz · question {qi + 1} of {quiz.length}</p>
      <h2 className={`mt-3 font-semibold text-slate-900 ${kids ? "font-kids text-3xl" : "font-display text-3xl"}`} data-testid="quiz-question">{q.q}</h2>
      <div className="mt-6 grid gap-3">
        {q.options.map((o: string, i: number) => {
          const state = ans === null ? "" : i === q.answer ? "border-emerald-500 bg-emerald-50" : i === ans ? "border-red-400 bg-red-50" : "opacity-60";
          return (
            <button key={i} data-testid={`quiz-option-${i}`} onClick={() => pick(i)}
              className={`flex items-center justify-between rounded-xl border-2 border-[#E2DBCE] bg-white px-5 py-4 text-left font-semibold transition-all hover:border-[#C86D51] ${state} ${kids ? "text-xl" : ""}`}>
              {o}{ans !== null && i === q.answer && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}{ans === i && i !== q.answer && <XCircle className="h-5 w-5 text-red-500" />}
            </button>
          );
        })}
      </div>
      {ans !== null && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-700" data-testid="quiz-explanation">{ans === q.answer ? "Well done! " : "Not quite. "}{q.explain}</p>
          <Button data-testid="quiz-next-button" onClick={next} className="rounded-full bg-[#1E293B]">{qi + 1 >= quiz.length ? "Finish" : "Next question"}</Button>
        </div>
      )}
    </div>
  );
}

function burst(kids?: boolean) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const colors = kids ? ["#E07A5F", "#81B29A", "#F2CC8F", "#3D405B", "#FFD166"] : ["#C86D51", "#5B7065", "#F5C98B", "#1E293B"];
  confetti({ particleCount: kids ? 140 : 70, spread: kids ? 100 : 70, origin: { y: 0.6 }, colors, scalar: kids ? 1.1 : 0.9, disableForReducedMotion: true });
  setTimeout(() => {
    confetti({ particleCount: kids ? 60 : 30, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: kids ? 60 : 30, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors });
  }, 250);
}

function NewSticker({ episode }: { episode: any }) {
  const fresh = !episode.completed;
  return (
    <motion.div initial={{ scale: 0.3, rotate: -20, opacity: 0 }} animate={{ scale: 1, rotate: -6, opacity: 1 }} transition={{ type: "spring", stiffness: 260, damping: 12, delay: 0.2 }}
      className="flex items-center gap-5 rounded-3xl bg-[#FFF8E8] p-5" data-testid="new-sticker">
      <img src={episode.scenes[0].image} alt="" className="h-24 w-24 rounded-full border-[5px] border-white object-cover shadow-xl" />
      <div className="rotate-6">
        <p className="font-kids text-2xl font-semibold text-[#E07A5F]">{fresh ? "New sticker earned!" : "Sticker collected again!"}</p>
        <Link to="/kids/stickers" data-testid="open-sticker-book-link" className="mt-1 inline-block font-semibold text-slate-700 underline decoration-[#81B29A] decoration-2 underline-offset-4">Open my sticker book</Link>
      </div>
    </motion.div>
  );
}

function Done({ episode, result, next, kids, base, badge }: any) {
  const [note, setNote] = useState("");
  useEffect(() => { burst(kids); }, []); // eslint-disable-line
  useEffect(() => { if (badge) setTimeout(() => burst(kids), 900); }, [badge]); // eslint-disable-line
  const save = async () => {
    try { await api.post("/notes", { ref: episode.ref, content: note, source: `Story: ${episode.title}` }); setNote(""); toast.success("Reflection saved to your notes"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className={`${kids ? "rounded-3xl bg-white p-8 shadow-xl" : "card-warm p-6 sm:p-10"} space-y-8`} data-testid="episode-complete">
      <div>
        {kids ? <div className="flex gap-1">{Array.from({ length: result.total }).map((_, i) => <Star key={i} className={`h-9 w-9 ${i < result.score ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
          : <Sparkles className="h-8 w-8 text-[#C86D51]" />}
        <h2 className={`mt-4 font-semibold text-slate-900 ${kids ? "font-kids text-4xl" : "font-display text-4xl"}`}>Episode complete</h2>
        <p className="mt-2 text-slate-600" data-testid="episode-score">You answered {result.score} of {result.total} correctly.</p>
      </div>
      {kids && <NewSticker episode={episode} />}
      {badge && (
        <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 220, damping: 11, delay: 0.5 }}
          className="flex items-center gap-5 rounded-3xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5" data-testid="series-badge-earned">
          <SeriesBadge badge={{ ...badge, earned: true }} size={88} />
          <div>
            <p className={`font-semibold text-amber-700 ${kids ? "font-kids text-2xl" : "font-display text-2xl"}`}>Series badge earned!</p>
            <p className="text-slate-700"><b>{badge.name}</b> · you finished every episode of {badge.series_title}.</p>
          </div>
        </motion.div>
      )}
      <div className="rounded-2xl bg-[#F3ECE0]/60 p-6">
        <p className="eyebrow">Dive deeper</p>
        <p className="mt-2 text-sm text-slate-700">This was a retelling. Read the full passage{kids ? " with a parent" : ""} in Scripture:</p>
        <div className="mt-3 flex flex-wrap gap-2">{episode.dive.refs.map((r: string) => <ScriptureRef key={r} refText={r} plain={kids} />)}</div>
        <ul className="mt-5 space-y-2">{episode.dive.prompts.map((p: string) => <li key={p} className="font-scripture text-lg italic text-slate-800">— {p}</li>)}</ul>
      </div>
      {!kids && (
        <div className="space-y-3">
          <Textarea data-testid="episode-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a reflection…" className="min-h-[100px] bg-white" />
          <div className="flex flex-wrap gap-3">
            <Button data-testid="episode-save-note-button" disabled={!note.trim()} onClick={save} className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]">Save to notes</Button>
            <Link data-testid="episode-ask-helper-link" to={`/deep-water?tab=ask&q=${encodeURIComponent(`What can I learn from ${episode.ref}?`)}`} className="rounded-full border border-[#E2DBCE] bg-white px-5 py-2 text-sm font-semibold hover:bg-[#F3ECE0]">Ask the study helper</Link>
          </div>
        </div>
      )}
      {next && <Link data-testid="next-episode-link" to={`${base}${next.id}`} className={`inline-flex items-center gap-2 rounded-full bg-[#1E293B] px-6 py-3 font-semibold text-white ${kids ? "text-lg" : ""}`}>Next: {next.title} <ChevronRight className="h-4 w-4" /></Link>}
    </motion.div>
  );
}

export function StoryPlayer({ episode, kids, base, onComplete }: { episode: any; kids?: boolean; base: string; onComplete: (r: Result) => Promise<any> }) {
  const [i, setI] = useState(0);
  const [stage, setStage] = useState<"scenes" | "quiz" | "done">("scenes");
  const [picked, setPicked] = useState<Record<number, number>>({});
  const [result, setResult] = useState<any>(null);
  const [next, setNext] = useState<any>(null);
  const [badge, setBadge] = useState<any>(null);
  useEffect(() => { setI(0); setStage("scenes"); setPicked({}); setResult(null); setNext(null); setBadge(null); }, [episode.id]);
  const scene = episode.scenes[i];
  const blocked = scene?.choice && picked[i] === undefined;

  const finish = async (score: number) => {
    const r = { score, total: episode.quiz.length, choices: Object.entries(picked).map(([s, o]) => `${s}:${o}`) };
    setResult(r);
    setStage("done");
    try {
      const res = await onComplete(r);
      setNext(res?.next);
      setBadge(res?.badge_earned || null);
      if (!kids) api.get("/streak").then((x) => announceStreak(x.data)).catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2" data-testid="scene-progress">
        {episode.scenes.map((_: any, k: number) => <span key={k} className={`h-1.5 flex-1 rounded-full transition-colors ${stage !== "scenes" || k <= i ? "bg-[#C86D51]" : "bg-[#E2DBCE]"}`} />)}
        <span className={`h-1.5 flex-1 rounded-full ${stage === "done" ? "bg-[#5B7065]" : "bg-[#E2DBCE]"}`} />
      </div>
      <AnimatePresence mode="wait">
        {stage === "scenes" && <Scene key={i} scene={scene} kids={kids} save={{ title: episode.title, link: `${base}${episode.id}` }} src={`${API}/narration/${episode.id}/${i}`} clip={episode.clip_url && i === episode.clip_scene ? `${API}${episode.clip_url}` : null} picked={picked[i]} onPick={(o: number) => setPicked({ ...picked, [i]: o })} />}
      </AnimatePresence>
      {stage === "scenes" && (
        <div className="flex items-center justify-between">
          <Button variant="ghost" data-testid="scene-prev-button" disabled={i === 0} onClick={() => setI(i - 1)}><ChevronLeft className="mr-1 h-4 w-4" />Back</Button>
          <span className="text-sm text-slate-500">Scene {i + 1} of {episode.scenes.length}</span>
          <Button data-testid="scene-next-button" disabled={blocked} onClick={() => (i + 1 < episode.scenes.length ? setI(i + 1) : setStage("quiz"))}
            className={`rounded-full ${kids ? "h-12 bg-[#E07A5F] px-7 text-lg hover:bg-[#d06a50]" : "bg-[#1E293B]"}`}>
            {blocked ? "Make a choice" : i + 1 < episode.scenes.length ? "Next scene" : "Take the quiz"}<ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}
      {stage === "quiz" && <Quiz quiz={episode.quiz} kids={kids} onDone={finish} />}
      {stage === "done" && result && <Done episode={episode} result={result} next={next} kids={kids} base={base} badge={badge} />}
    </div>
  );
}
